import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  CircleAlert,
  CirclePause,
  CircleSlash,
  Maximize,
  Minimize,
  Pause,
  Play,
  Triangle,
} from "lucide-react";
import { api, tokenUrl } from "@/lib/api";
import { dateLocale, t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { LiveClock } from "@/components/facilities/LiveClock";
import { KpiTiles } from "@/components/facilities/KpiTiles";
import { EventFeed } from "@/components/facilities/EventFeed";
import { useFacilityStatus } from "@/hooks/useFacilityStatus";
import { useAuth } from "@/hooks/useAuth";
import { useFullscreen } from "@/hooks/useFullscreen";
import {
  hasFeature,
  parsePolygonGeometry,
  parseRectGeometry,
  type FacilityMap,
  type MarkerStatusCode,
  type MarkerStatusEntry,
  type ShowMode,
} from "@/lib/types";

const STATUS_COLOR: Record<MarkerStatusCode, string> = {
  ok: "bg-success",
  idle: "bg-muted-foreground",
  warn: "bg-warning",
  error: "bg-destructive",
  stale: "bg-muted-foreground/40",
};

// Für Rect-Marker: tinted Hintergrund + Border in derselben Farbe.
// „Bereit" und „Veraltet" sind beide grau; Veraltet ist gestrichelt und fast
// leer, weil dort gar nichts mehr ankommt.
const STATUS_RECT: Record<MarkerStatusCode, string> = {
  ok: "border-success bg-success/25",
  idle: "border-muted-foreground bg-muted-foreground/20",
  warn: "border-warning bg-warning/25",
  error: "border-destructive bg-destructive/25",
  stale: "border-dashed border-muted-foreground/60 bg-muted-foreground/5",
};

// Für SVG-Polygone: direkte CSS-Farbwerte (Tailwind-Klassen funktionieren in SVG nicht)
const STATUS_SVG: Record<MarkerStatusCode, string> = {
  ok: "hsl(var(--success))",
  idle: "hsl(var(--muted-foreground))",
  warn: "hsl(var(--warning))",
  error: "hsl(var(--destructive))",
  stale: "hsl(var(--muted-foreground))",
};

const STATUS_LABEL: Record<MarkerStatusCode, string> = {
  ok: t("OK"),
  idle: t("Bereit"),
  warn: t("Warnung"),
  error: t("Fehler"),
  stale: t("Veraltet"),
};

const STATUS_ICON: Record<MarkerStatusCode, typeof CheckCircle2> = {
  ok: CheckCircle2,
  idle: CirclePause,
  warn: Triangle,
  error: CircleAlert,
  stale: CircleSlash,
};

const MIN_INTERVAL = 2;
const MAX_INTERVAL = 600;
const DEFAULT_INTERVAL = 10;

export function FacilitiesLivePage() {
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const [maps, setMaps] = useState<FacilityMap[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [hovered, setHovered] = useState<MarkerStatusEntry | null>(null);
  const [showMode, setShowMode] = useState<ShowMode | null>(null);
  const { data, state } = useFacilityStatus(selectedId);

  // Show-Mode-Features
  const showClock = hasFeature(showMode, "live_clock");
  const showTiles = hasFeature(showMode, "tiles");
  const showFeed = hasFeature(showMode, "event_feed");
  const useCrossfade = hasFeature(showMode, "crossfade");
  const useMarkerPulse = hasFeature(showMode, "marker_pulse") || showMode == null;

  const { ref: fsRef, isFullscreen, toggle: toggleFs } = useFullscreen<HTMLDivElement>();

  const initialMapParam = params.get("map");
  const showmodeParam = params.get("showmode");
  const rotateRaw = params.get("rotate");
  const rotateOn = rotateRaw === "1" || rotateRaw === "true";
  const intervalParam = parseInt(params.get("interval") ?? "", 10);
  const intervalSec = clampInterval(intervalParam);

  // ── Maps laden (einmalig), Initial-Map aus URL anwenden ────────────────
  useEffect(() => {
    void api.get<{ maps: FacilityMap[] }>("/api/maps").then((r) => {
      const list = r.maps ?? [];
      setMaps(list);
      if (list.length === 0) return;
      const fromUrl = initialMapParam ? parseInt(initialMapParam, 10) : NaN;
      const initial = Number.isFinite(fromUrl) && list.some((m) => m.id === fromUrl)
        ? fromUrl
        : list[0].id;
      setSelectedId(initial);
    });
    // Initial-Map-Param nur beim Mount auswerten — spätere Manual-Selects
    // schreiben den URL-State separat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Show-Mode laden ────────────────────────────────────────────────────
  useEffect(() => {
    if (!showmodeParam) {
      setShowMode(null);
      return;
    }
    let cancelled = false;
    void api
      .get<ShowMode>(`/api/showmodes/by-name/${encodeURIComponent(showmodeParam)}`)
      .then((m) => {
        if (!cancelled) setShowMode(m);
      })
      .catch(() => {
        if (!cancelled) setShowMode(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showmodeParam]);

  // ── Auto-Rotate ────────────────────────────────────────────────────────
  const rotateTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!rotateOn || maps.length < 2 || selectedId == null) return;
    rotateTimerRef.current = window.setInterval(() => {
      setSelectedId((cur) => {
        if (cur == null) return cur;
        const idx = maps.findIndex((m) => m.id === cur);
        const nextIdx = idx === -1 ? 0 : (idx + 1) % maps.length;
        const nextId = maps[nextIdx].id;
        // URL ohne Volltext-Replace nachziehen
        updateParam(setParams, "map", String(nextId));
        return nextId;
      });
    }, intervalSec * 1000);
    return () => {
      if (rotateTimerRef.current) window.clearInterval(rotateTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotateOn, intervalSec, maps, selectedId]);

  // ── Manueller Map-Switch synct mit URL ─────────────────────────────────
  const onSelect = useCallback(
    (id: number | null) => {
      setSelectedId(id);
      if (id != null) updateParam(setParams, "map", String(id));
      else clearParam(setParams, "map");
    },
    [setParams],
  );

  const toggleRotate = useCallback(() => {
    const next = !rotateOn;
    if (next) updateParam(setParams, "rotate", "1");
    else clearParam(setParams, "rotate");
  }, [rotateOn, setParams]);

  const onIntervalChange = (n: number) => {
    const v = clampInterval(n);
    if (v === DEFAULT_INTERVAL) clearParam(setParams, "interval");
    else updateParam(setParams, "interval", String(v));
  };

  const selectedMap = useMemo(
    () => maps.find((m) => m.id === selectedId) ?? null,
    [maps, selectedId],
  );

  const counts = data?.counts ?? {};
  const headerTitle = showMode?.header_title ?? selectedMap?.name ?? t("Anlage");
  const headerSubtitle = showMode?.header_subtitle ?? subtitleFor(state);

  return (
    <div
      ref={fsRef}
      className="flex flex-col gap-4 p-6 [&:fullscreen]:overflow-auto [&:fullscreen]:bg-background"
    >
      <Card
        title={t("Plan auswählen")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {showClock ? <LiveClock /> : null}
            <select
              value={selectedId ?? ""}
              onChange={(e) => onSelect(parseInt(e.target.value, 10) || null)}
              className="h-8 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            >
              {maps.length === 0 ? <option value="">{t("— keine Pläne —")}</option> : null}
              {maps.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={toggleRotate}
              disabled={maps.length < 2}
              title={rotateOn ? t("Auto-Rotate stoppen") : t("Auto-Rotate starten")}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs hover:bg-muted disabled:opacity-50"
            >
              {rotateOn ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
              <input
                type="number"
                min={MIN_INTERVAL}
                max={MAX_INTERVAL}
                value={intervalSec}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onIntervalChange(parseInt(e.target.value, 10) || DEFAULT_INTERVAL)}
                className="h-6 w-12 rounded border border-border bg-background px-1 text-right text-xs tabular-nums outline-none"
              />
              <span className="text-muted-foreground">s</span>
            </button>
            <button
              type="button"
              onClick={toggleFs}
              title={isFullscreen ? t("Vollbild verlassen (Esc)") : t("Vollbild")}
              aria-label={isFullscreen ? t("Vollbild verlassen") : t("Vollbild")}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs hover:bg-muted"
            >
              {isFullscreen ? <Minimize className="h-3.5 w-3.5" /> : <Maximize className="h-3.5 w-3.5" />}
            </button>
            {showMode ? (
              <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">
                {t("Show-Mode: {name}", { name: showMode.name })}
              </span>
            ) : null}
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <CountTile label={t("OK")} value={counts.ok ?? 0} tone="success" Icon={CheckCircle2} />
          <CountTile label={t("Bereit")} value={counts.idle ?? 0} tone="muted" Icon={CirclePause} />
          <CountTile label={t("Warnung")} value={counts.warn ?? 0} tone="warning" Icon={Triangle} />
          <CountTile label={t("Fehler")} value={counts.error ?? 0} tone="destructive" Icon={CircleAlert} />
          <CountTile label={t("Veraltet")} value={counts.stale ?? 0} tone="muted" Icon={CircleSlash} />
        </div>
      </Card>

      {showTiles ? <KpiTiles /> : null}

      <div className="flex items-stretch gap-4">
        <Card title={headerTitle} description={headerSubtitle} className="flex-1">
          {selectedMap ? (
            <div
              key={useCrossfade ? selectedMap.id : "static"}
              className={
                // block + w-full statt inline-block/max-w-full: SVGs ohne
                // width/height-Attribute kollabieren sonst in Safari zu 0×0.
                "relative overflow-hidden rounded-md border border-border bg-muted/40 " +
                (useCrossfade ? "animate-in fade-in duration-700" : "")
              }
            >
              <img
                src={tokenUrl(`/api/maps/${selectedMap.id}/image?v=${encodeURIComponent(selectedMap.image_path)}`)}
                alt={selectedMap.name}
                className="block w-full"
                draggable={false}
              />
              {data?.markers.map((m) => renderMarker(m, useMarkerPulse, setHovered))}

              {/* Polygon-Layer als SVG-Overlay, viewBox 0..100 = % */}
              {data && data.markers.some((m) => m.shape === "polygon") ? (
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  className="pointer-events-none absolute inset-0 h-full w-full"
                >
                  {data.markers
                    .filter((m) => m.shape === "polygon")
                    .map((m) => {
                      const geo = parsePolygonGeometry(m.geometry);
                      if (!geo) return null;
                      const pulse = useMarkerPulse && (m.status === "warn" || m.status === "error");
                      return (
                        <polygon
                          key={m.id}
                          points={geo.points.map((p) => p.join(",")).join(" ")}
                          fill={STATUS_SVG[m.status]}
                          fillOpacity={m.status === "stale" ? 0.05 : 0.32}
                          stroke={STATUS_SVG[m.status]}
                          strokeDasharray={m.status === "stale" ? "4 3" : undefined}
                          strokeWidth={0.6}
                          vectorEffect="non-scaling-stroke"
                          className={"pointer-events-auto cursor-pointer " + (pulse ? "animate-pulse" : "")}
                          onMouseEnter={() => setHovered(m)}
                          onMouseLeave={() => setHovered(null)}
                        />
                      );
                    })}
                </svg>
              ) : null}

              {/* Zustandstext der GreenBox, über allen Formen */}
              {data?.markers.map((m) => <StatusText key={`text-${m.id}`} marker={m} />)}

              {hovered ? <MarkerTooltip marker={hovered} /> : null}
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              {t("Lade unter Pläne einen Plan hoch und setze Marker.")}
            </div>
          )}
        </Card>

        {showFeed ? <EventFeed enabled={!!token} minLevel={showMode?.min_level ?? null} /> : null}
      </div>

      {showMode?.footer_text ? (
        <div className="px-2 text-right text-[11px] text-muted-foreground">
          {showMode.footer_text}
        </div>
      ) : null}
    </div>
  );
}

function subtitleFor(state: "idle" | "connecting" | "open" | "closed"): string {
  switch (state) {
    case "open":
      return t("Live-Status über SSE — Updates erscheinen automatisch.");
    case "connecting":
      return t("Verbinde…");
    case "closed":
      return t("Verbindung getrennt — wird wiederhergestellt");
    default:
      return t("Wähle einen Plan, um den Status zu sehen.");
  }
}

function clampInterval(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_INTERVAL;
  return Math.max(MIN_INTERVAL, Math.min(MAX_INTERVAL, n));
}

function updateParam(
  setParams: (next: URLSearchParams, opts?: { replace?: boolean }) => void,
  key: string,
  value: string,
) {
  const sp = new URLSearchParams(window.location.search);
  sp.set(key, value);
  setParams(sp, { replace: true });
}

function clearParam(
  setParams: (next: URLSearchParams, opts?: { replace?: boolean }) => void,
  key: string,
) {
  const sp = new URLSearchParams(window.location.search);
  sp.delete(key);
  setParams(sp, { replace: true });
}

function renderMarker(
  m: MarkerStatusEntry,
  useMarkerPulse: boolean,
  setHovered: (m: MarkerStatusEntry | null) => void,
) {
  const pulse = useMarkerPulse && (m.status === "warn" || m.status === "error");
  const label = m.label ?? t("Marker {id}", { id: m.id });

  if (m.shape === "rect") {
    const geo = parseRectGeometry(m.geometry);
    if (!geo) return null;
    return (
      <button
        key={m.id}
        type="button"
        style={{ left: `${m.x_pct}%`, top: `${m.y_pct}%`, width: `${geo.w}%`, height: `${geo.h}%` }}
        onMouseEnter={() => setHovered(m)}
        onMouseLeave={() => setHovered(null)}
        aria-label={label}
        className={
          "absolute rounded border-2 shadow-sm transition-colors " +
          STATUS_RECT[m.status] +
          (pulse ? " animate-pulse" : "")
        }
      />
    );
  }

  if (m.shape === "polygon") {
    // Polygon-Shape selbst kommt aus dem SVG-Overlay weiter unten — hier nur
    // ein kleiner Anchor am ersten Punkt für Hover, falls die Form klein ist.
    return null;
  }

  // Point default
  return (
    <button
      key={m.id}
      type="button"
      style={{ left: `${m.x_pct}%`, top: `${m.y_pct}%` }}
      onMouseEnter={() => setHovered(m)}
      onMouseLeave={() => setHovered(null)}
      aria-label={label}
      className={
        "absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card shadow " +
        STATUS_COLOR[m.status] +
        (pulse ? " animate-pulse" : "")
      }
    />
  );
}

// Schild mit dem Zellzustand direkt auf dem Plan: mittig im Rechteck oder
// Polygon (bis zu zwei Zeilen), bei Punkten daneben in einer Zeile (am
// rechten Rand links davon). Was nicht passt, wird abgeschnitten und steht
// vollständig im Tooltip. Die Maus geht
// durch das Schild hindurch auf den Marker.
function StatusText({ marker: m }: { marker: MarkerStatusEntry }) {
  if (!m.status_text) return null;
  let style: CSSProperties;
  let lage = "-translate-x-1/2 -translate-y-1/2 line-clamp-2 whitespace-normal break-words text-center";
  if (m.shape === "rect") {
    const geo = parseRectGeometry(m.geometry);
    if (!geo) return null;
    style = {
      left: `${m.x_pct + geo.w / 2}%`,
      top: `${m.y_pct + geo.h / 2}%`,
      maxWidth: `calc(${geo.w}% - 6px)`,
    };
  } else if (m.shape === "polygon") {
    const geo = parsePolygonGeometry(m.geometry);
    if (!geo) return null;
    const xs = geo.points.map((p) => p[0]);
    const ys = geo.points.map((p) => p[1]);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    style = {
      left: `${(x0 + x1) / 2}%`,
      top: `${(Math.min(...ys) + Math.max(...ys)) / 2}%`,
      maxWidth: `calc(${x1 - x0}% - 6px)`,
    };
  } else {
    const links = m.x_pct > 70;
    style = links
      ? { right: `calc(${100 - m.x_pct}% + 12px)`, top: `${m.y_pct}%`, maxWidth: `min(14rem, calc(${m.x_pct}% - 12px))` }
      : { left: `calc(${m.x_pct}% + 12px)`, top: `${m.y_pct}%`, maxWidth: `min(14rem, calc(${100 - m.x_pct}% - 12px))` };
    lage = "-translate-y-1/2 truncate whitespace-nowrap";
  }
  return (
    <span
      style={style}
      className={
        "pointer-events-none absolute z-[1] rounded bg-card/90 px-1.5 py-0.5 " +
        "text-[11px] font-medium leading-tight text-foreground shadow-sm " +
        lage
      }
    >
      {m.status_text}
    </span>
  );
}

function MarkerTooltip({ marker }: { marker: MarkerStatusEntry }) {
  const Icon = STATUS_ICON[marker.status];
  return (
    <div
      style={{ left: `${marker.x_pct}%`, top: `${marker.y_pct}%` }}
      className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-[calc(100%+12px)] rounded-md border border-border bg-card px-3 py-2 text-xs shadow-xl"
    >
      <div className="flex items-center gap-1.5 font-medium">
        <Icon className="h-3.5 w-3.5" />
        {marker.label ?? marker.client_id ?? t("Marker {id}", { id: marker.id })}
      </div>
      <div className="text-[11px] text-muted-foreground">
        {STATUS_LABEL[marker.status]}
        {marker.last_timestamp
          ? ` · ${new Date(marker.last_timestamp).toLocaleTimeString(dateLocale())}`
          : ""}
      </div>
      {marker.last_message ? (
        <div className="mt-1 max-w-[260px] truncate font-mono text-[11px]">{marker.last_message}</div>
      ) : null}
    </div>
  );
}

function CountTile({
  label,
  value,
  tone,
  Icon,
}: {
  label: string;
  value: number;
  tone: "success" | "warning" | "destructive" | "muted";
  Icon: typeof CheckCircle2;
}) {
  const toneClass =
    tone === "success"
      ? "text-success"
      : tone === "warning"
        ? "text-warning"
        : tone === "destructive"
          ? "text-destructive"
          : "text-muted-foreground";
  return (
    <div className="rounded-md bg-muted/60 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Icon className={"h-3 w-3 " + toneClass} />
        {label}
      </div>
      <div className={`text-lg font-medium tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}
